import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/feature/PageHeader';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { useSEO } from '@/hooks/useSEO';
import { topicItems, formatViews } from '@/hooks/useContent';

const PAGE_SIZE = 5;

const tagCloud = ['影视', '活动', '音乐', '短剧', '讨论', '治愈', '虚拟偶像', '分享', '片单'];

export default function TopicCategoryPage() {
  const [activeTag, setActiveTag] = useState('全部');
  const [page, setPage] = useState(1);

  useSEO(
    '互动话题 - 一起艹',
    '社区热门话题交流：聊剧集、聊现场、聊创作，参与人数最多的话题都在这里，欢迎加入讨论。',
    '互动话题,娱乐社区,讨论,话题交流,一起艹',
  );

  const filtered = useMemo(() => {
    return activeTag === '全部' ? topicItems : topicItems.filter((item) => item.tags.includes(activeTag));
  }, [activeTag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const reset = () => {
    setActiveTag('全部');
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHeader
        title="互动话题"
        subtitle="社区热门话题交流，看看大家都在聊什么，也可以进入话题留下你的看法。"
        crumbs={[{ label: '首页', to: '/' }, { label: '互动话题' }]}
        icon="ri-chat-3-line"
      />

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="p-5 md:p-6 rounded-lg bg-accent-50 border border-accent-200 mb-8">
          <h2 className="flex items-center gap-2 font-heading text-lg md:text-xl text-foreground-950 mb-4">
            <i className="ri-hashtag text-accent-600" />
            热门话题标签
          </h2>
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={() => {
                setActiveTag('全部');
                setPage(1);
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                activeTag === '全部'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-accent-400'
              }`}
            >
              全部话题
            </button>
            {tagCloud.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  setActiveTag(tag);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                  activeTag === tag
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-accent-400 hover:text-accent-800'
                }`}
              >
                # {tag}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-chat-3-line"
            title="暂无符合条件的话题"
            description="这个标签下暂时还没有话题，换个标签看看，或重置筛选条件。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {pageItems.map((item) => (
                <div
                  key={item.id}
                  className="p-5 md:p-6 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="hidden md:block w-24 h-24 rounded-md overflow-hidden bg-background-200 shrink-0">
                      <img src={item.cover} alt={item.title} title={`${item.title} - 一起艹`} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/detail/${item.id}`}
                        className="font-medium text-base md:text-lg text-foreground-950 hover:text-primary-600 cursor-pointer transition-colors"
                      >
                        {item.title}
                      </Link>
                      <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{item.summary}</p>
                      <div className="mt-4 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-smile-2-line" />
                          {formatViews(item.participants)} 人参与
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" />
                          最新回复 {item.lastReplyAt}
                        </span>
                        <span className="flex flex-wrap gap-1.5">
                          {item.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">
                              # {tag}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/detail/${item.id}`}
                      className="self-start inline-flex items-center gap-1 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-colors"
                    >
                      参与讨论
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}